import { FormHeader } from "app/components/atoms/layout";
import { Select } from "app/components/atoms/select";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import { DateRangeInputMolecule } from "app/components/molecules/input";
import api from "app/integration/api";
import { useFetcher } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { defaultReportDates } from "app/common/reportDateRange";
import { angka } from "app/common/reportNumber";

const breadcrumbItems = [
  { label: "Laporan Toko", href: "#" },
  { label: "Penjualan per Kategori", href: "#" },
];

function StoreReportSalesByCategoryPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [queryParams, setQueryParams] = useState({
    warehouse_id: null,
    ...defaultReportDates(),
  });

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await api.warehouse.index({ limit: 1000 });
        setWarehouses(response?.data ?? []);
      } catch (err) {
        setWarehouses([]);
      }
    };
    loadWarehouses();
  }, []);

  const warehouseOptions = [
    { value: null, label: "Semua Outlet" },
    ...warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.name,
    })),
  ];

  const [rows, , loader] = useFetcher({
    modelName: "storeReport",
    methodName: "salesByCategory",
    payload: { id: queryParams },
  });

  const exportExcel = () => {
    const dataForExcel = (Array.isArray(rows) ? rows : []).map((row) => ({
      Kategori: row.category_name,
      "Qty Terjual": row.qty_sold,
      Subtotal: angka(row.sub_total),
      Diskon: angka(row.discount),
      "Net Sales": row.net_sales,
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel, {
      header: ["Kategori", "Qty Terjual", "Subtotal", "Diskon", "Net Sales"],
    });
    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 12 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Penjualan per Kategori"
    );
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const formattedDate = new Date().toISOString().split("T")[0];
    link.download = `laporan_penjualan_per_kategori_${formattedDate}.xlsx`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <MainLayout
      activeSidebarNavigation="store-report"
      activeSidebarChild="sales-by-category"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Laporan Penjualan per Kategori"
      headingButtons={[
        {
          label: "Export Excel",
          type: "button",
          onClick: exportExcel,
        },
      ]}
    >
      <Table.Container>
        <Table.SearchBarWrapper>
          <FormHeader>
            <div className="col-span-2">
              <DateRangeInputMolecule testId="store-report-sales-by-category-date-range"
                label="Tanggal"
                from={queryParams.start_date}
                to={queryParams.end_date}
                onFromChange={(e) =>
                  setQueryParams({
                    ...queryParams,
                    start_date: e.target.value,
                  })
                }
                onToChange={(e) =>
                  setQueryParams({ ...queryParams, end_date: e.target.value })
                }
              />
            </div>
            <Select testId="store-report-sales-by-category-outlet-select"
              label="Outlet"
              placeholder="Filter Outlet"
              options={warehouseOptions}
              value={queryParams.warehouse_id}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  warehouse_id: e.target.value,
                })
              }
              absolute={false}
            />
          </FormHeader>
        </Table.SearchBarWrapper>
        <Table.Wrapper asIndex={true}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Kategori</Table.Heading>
              <Table.Heading>
                <p className="text-right">Qty Terjual</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Subtotal</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Diskon</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Net Sales</p>
              </Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {Array.isArray(rows) &&
              rows.map((row, index) => (
                <Table.BodyRow key={index}>
                  <Table.Cell>{row.category_name ?? "-"}</Table.Cell>
                  <Table.Cell>
                    <p className="text-right">{row.qty_sold}</p>
                  </Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      <Typography.Currency number={angka(row.sub_total)} />
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      <Typography.Currency number={angka(row.discount)} />
                    </p>
                  </Table.Cell>
                  <Table.Cell fontWeight="semibold">
                    <p className="text-right">
                      <Typography.Currency number={row.net_sales} />
                    </p>
                  </Table.Cell>
                </Table.BodyRow>
              ))}
          </Table.Body>
        </Table.Wrapper>
      </Table.Container>
      {loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Memuat data...
        </p>
      )}
      {Array.isArray(rows) && rows.length === 0 && !loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Tidak ada data untuk filter ini.
        </p>
      )}
    </MainLayout>
  );
}

export default StoreReportSalesByCategoryPage;
